import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckCircle, BarChart3, Building2, Shield, FileText, ArrowRight } from "lucide-react";
import { fmtMoney } from "../../lib/money";

/**
 * QENVA · Priorités du Jour — 2×2 grid of ranked priority cards.
 *
 *  ┌──────────────────────┬──────────────────────┐
 *  │ 🏢 #1   [Critique]   │ 📊 #2   [Urgent]    │
 *  │ Renouvellement bail  │ Correction covenant │
 *  │ commercial           │ bancaire             │
 *  │ IMPACT       Voir →  │ IMPACT       Voir → │
 *  │ 72 000 $             │ 48 000 $            │
 *  ├──────────────────────┼──────────────────────┤
 *  │ 🛡 #3   [Urgent]    │ 📄 #4   [À faire]   │
 *  │ Révision couverture  │ Révision contrat    │
 *  │ assurance            │ fournisseur         │
 *  │ IMPACT       Voir →  │ IMPACT       Voir → │
 *  │ 12 000 $             │ 2 400 $             │
 *  └──────────────────────┴──────────────────────┘
 */
const SOURCE_ICON = {
  deadline:       Building2,
  recommendation: FileText,
  finding:        BarChart3,
  predictive:     Shield,
  default:        Shield,
};

export default function PrioritiesTodayGrid({ priorities, onAction, testId = "priorities-today" }) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const items = priorities || [];

  const URGENCY_DEF = {
    critical: { label: t("priorities_today.urgency_critical"), pill: "bg-rose-50 text-rose-600 ring-1 ring-rose-200" },
    urgent:   { label: t("priorities_today.urgency_urgent"),   pill: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" },
    todo:     { label: t("priorities_today.urgency_todo"),     pill: "bg-qenva-blue/10 text-qenva-blue ring-1 ring-qenva-blue/20" },
  };

  return (
    <section
      className="rounded-2xl border border-zinc-200 bg-white p-5"
      data-testid={testId}
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckCircle size={15} className="text-qenva-blue" />
          <h2 className="font-display font-bold text-qenva-navy text-[15px] tracking-tight">
            {t("priorities_today.title")}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => navigate("/alerts")}
          data-testid={`${testId}-see-alerts`}
          className="text-qenva-blue text-[12px] font-semibold hover:underline inline-flex items-center gap-1"
        >
          {t("priorities_today.see_alerts")} <ArrowRight size={11} />
        </button>
      </header>

      {items.length === 0 ? (
        <p className="text-zinc-400 text-[13px] text-center py-6" data-testid={`${testId}-empty`}>
          {t("priorities_today.empty")}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2.5" data-testid={`${testId}-grid`}>
          {items.map((item) => {
            const Icon = SOURCE_ICON[item.source_type] || SOURCE_ICON.default;
            const urg = URGENCY_DEF[item.urgency] || URGENCY_DEF.todo;
            return (
              <article
                key={item.rank}
                className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-3 hover:bg-white hover:shadow-sm transition-all"
                data-testid={`${testId}-item-${item.rank}`}
              >
                <header className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-white border border-zinc-200 grid place-items-center text-zinc-600">
                      <Icon size={13} strokeWidth={1.75} />
                    </div>
                    <span className="text-[11px] font-bold text-zinc-500">#{item.rank}</span>
                  </div>
                  <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${urg.pill}`}>
                    {urg.label}
                  </span>
                </header>
                <p className="font-display font-bold text-qenva-navy text-[13.5px] tracking-tight mb-2 line-clamp-2" title={item.title}>
                  {item.title}
                </p>
                <div className="flex items-center justify-between pt-2 border-t border-zinc-200/70">
                  <div className="min-w-0">
                    <p className="text-[9.5px] uppercase tracking-wider font-bold text-zinc-500 leading-tight">{t("priorities_today.impact")}</p>
                    <p className="text-[13px] font-display font-bold text-qenva-navy tabular-nums leading-tight">
                      {fmtMoney(item.impact_amount, item.impact_currency)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onAction ? onAction(item) : navigate(`/alerts?id=${item.alert_id}`)}
                    data-testid={`${testId}-item-${item.rank}-cta`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11.5px] font-bold text-qenva-blue hover:bg-qenva-blue/10 transition-colors shrink-0"
                  >
                    {t("common:actions.view")}
                    <ArrowRight size={11} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
